// SpecificationManagement.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiSearch,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiArrowUp,
  FiArrowDown,
  FiCalendar,
  FiCheck,
  FiX,
  FiStar,
  FiHome,
  FiSettings,
  FiLayers,
  FiPackage,
  FiTool,
  FiCpu,
  FiMonitor,
  FiHardDrive,
  FiSmartphone,
  FiCamera,
  FiBattery,
  FiCpu as FiProcessor,
  FiMemory,
  FiDatabase,
  FiServer,
  FiGrid,
  FiBox,
  FiArchive,
  FiTag,
  FiList,
  FiClipboard,
  FiFileText,
  FiCode,
  FiType,
  FiHash,
} from "react-icons/fi";
import {
  FaRegCheckCircle,
  FaRegTimesCircle,
  FaMemory,
  FaSdCard,
  FaMicrochip,
  FaBolt,
  FaWeight,
  FaRulerCombined,
  FaTemperatureHigh,
  FaNetworkWired,
} from "react-icons/fa";
import {
  GiProcessor,
  GiCircuitry,
  GiMaterialsScience,
  GiChemicalTank,
  GiAbstract,
  GiThreeLeaves,
  GiSpellBook,
  GiStoneBlock,
} from "react-icons/gi";
import {
  MdOutlineStorage,
  MdOutlineScreenshotMonitor,
  MdOutlineColorLens,
  MdOutlinePrecisionManufacturing,
  MdOutlineFactory,
  MdOutlineScience,
  MdOutlineEngineering,
  MdOutlineDesignServices,
} from "react-icons/md";

const SpecificationManagement = () => {
  const [specifications, setSpecifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingSpecification, setEditingSpecification] = useState(null);
  const [viewingSpecification, setViewingSpecification] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "cr_date",
    direction: "desc",
  });
  const [processing, setProcessing] = useState(false);

  const [formData, setFormData] = useState({
    specification_name: "",
    status: "active",
  });

  const API_BASE = "https://dev.mithahomes.com/backend/api/specification";

  // Statistics
  const stats = {
    total: specifications.length,
    active: specifications.filter((s) => s.status === "active").length,
    inactive: specifications.filter((s) => s.status === "inactive").length,
    recent: specifications.filter((s) => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(s.cr_date) > weekAgo;
    }).length,
  };

  // Fetch all specifications
  const fetchSpecifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/all`);
      if (response.data.success) {
        setSpecifications(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching specifications:", error);
      toast.error("❌ Error loading specifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecifications();
  }, []);

  // Filter specifications
  const filteredSpecifications = specifications.filter((spec) => {
    const matchesSearch =
      spec.specification_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      spec.sid?.toString().includes(searchTerm);
    const matchesStatus =
      filterStatus === "all" || spec.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Sort specifications
  const sortedSpecifications = React.useMemo(() => {
    if (!sortConfig.key) return filteredSpecifications;

    return [...filteredSpecifications].sort((a, b) => {
      // Handle date sorting
      if (sortConfig.key === "cr_date" || sortConfig.key === "update_time") {
        const dateA = new Date(a[sortConfig.key]);
        const dateB = new Date(b[sortConfig.key]);
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }

      // Handle numeric sorting
      if (sortConfig.key === "sid") {
        return sortConfig.direction === "asc" ? a.sid - b.sid : b.sid - a.sid;
      }

      // Handle string sorting
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredSpecifications, sortConfig]);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      specification_name: "",
      status: "active",
    });
    setEditingSpecification(null);
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.specification_name.trim()) {
      toast.error("❌ Please enter a specification name");
      return false;
    }
    if (formData.specification_name.trim().length < 2) {
      toast.error("❌ Specification name must be at least 2 characters long");
      return false;
    }
    if (formData.specification_name.trim().length > 100) {
      toast.error("❌ Specification name must be less than 100 characters");
      return false;
    }
    return true;
  };

  // ✅ Add new specification or update existing
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setProcessing(true);

      let response;
      if (editingSpecification) {
        // Update specification
        response = await axios.put(
          `${API_BASE}/all/${editingSpecification.sid}`,
          formData,
        );
      } else {
        // Create new specification
        response = await axios.post(`${API_BASE}/add`, formData);
      }

      if (response.data.success) {
        toast.success(
          `✅ ${editingSpecification ? "Specification updated" : "Specification added"} successfully!`,
        );
        setShowModal(false);
        resetForm();
        fetchSpecifications();
      } else {
        throw new Error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving specification:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error saving specification";
      if (error.response?.status === 409) {
        toast.error("❌ Specification name already exists");
      } else {
        toast.error(`❌ ${errorMessage}`);
      }
    } finally {
      setProcessing(false);
    }
  };

  // ✅ Edit specification
  const handleEdit = (specification) => {
    setEditingSpecification(specification);
    setFormData({
      specification_name: specification.specification_name || "",
      status: specification.status || "active",
    });
    setShowModal(true);
  };

  // View specification details
  const handleView = (specification) => {
    setViewingSpecification(specification);
    setShowViewModal(true);
  };

  // ✅ Toggle status
  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      const response = await axios.put(`${API_BASE}/all/${id}`, {
        status: newStatus,
      });

      if (response.data.success) {
        toast.success(
          `📝 Specification ${newStatus === "active" ? "activated" : "deactivated"}!`,
        );
        fetchSpecifications();
      } else {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error updating status";
      toast.error(`❌ ${errorMessage}`);
    }
  };

  // ✅ Delete specification
  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this specification? This action cannot be undone.",
      )
    ) {
      try {
        const response = await axios.delete(`${API_BASE}/del/${id}`);

        if (response.data.success) {
          toast.success("🗑️ Specification deleted successfully!");
          fetchSpecifications();
        } else {
          throw new Error(response.data.message || "Delete failed");
        }
      } catch (error) {
        console.error("Error deleting specification:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error deleting specification";
        toast.error(`❌ ${errorMessage}`);
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format time
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status color and icon
  const getStatusConfig = (status) => {
    if (status === "active") {
      return {
        background: "#d1fae5",
        color: "#065f46",
        border: "#a7f3d0",
        icon: <FaRegCheckCircle />,
        text: "Active",
      };
    } else {
      return {
        background: "#fee2e2",
        color: "#dc2626",
        border: "#fecaca",
        icon: <FaRegTimesCircle />,
        text: "Inactive",
      };
    }
  };

  // Get specification icon based on name
  const getSpecificationIcon = (specName) => {
    const name = specName.toLowerCase();

    // Technical specifications
    if (name.includes("processor") || name.includes("cpu")) {
      return <GiProcessor style={{ color: "#3b82f6", fontSize: "1.2rem" }} />;
    } else if (name.includes("memory") || name.includes("ram")) {
      return <FaMemory style={{ color: "#8b5cf6", fontSize: "1.2rem" }} />;
    } else if (
      name.includes("storage") ||
      name.includes("hard drive") ||
      name.includes("ssd")
    ) {
      return <FiHardDrive style={{ color: "#10b981", fontSize: "1.2rem" }} />;
    } else if (
      name.includes("display") ||
      name.includes("screen") ||
      name.includes("monitor")
    ) {
      return <FiMonitor style={{ color: "#ef4444", fontSize: "1.2rem" }} />;
    } else if (name.includes("battery")) {
      return <FiBattery style={{ color: "#84cc16", fontSize: "1.2rem" }} />;
    } else if (name.includes("camera")) {
      return <FiCamera style={{ color: "#ec4899", fontSize: "1.2rem" }} />;
    } else if (name.includes("weight")) {
      return <FaWeight style={{ color: "#64748b", fontSize: "1.2rem" }} />;
    } else if (name.includes("dimension") || name.includes("size")) {
      return (
        <FaRulerCombined style={{ color: "#f59e0b", fontSize: "1.2rem" }} />
      );
    } else if (name.includes("color")) {
      return (
        <MdOutlineColorLens style={{ color: "#ef4444", fontSize: "1.2rem" }} />
      );
    } else if (name.includes("resolution")) {
      return (
        <MdOutlineScreenshotMonitor
          style={{ color: "#6366f1", fontSize: "1.2rem" }}
        />
      );

      // Material specifications
    } else if (name.includes("material")) {
      return (
        <GiMaterialsScience style={{ color: "#06b6d4", fontSize: "1.2rem" }} />
      );
    } else if (name.includes("finish") || name.includes("coating")) {
      return (
        <MdOutlineDesignServices
          style={{ color: "#8b5cf6", fontSize: "1.2rem" }}
        />
      );
    } else if (name.includes("grade")) {
      return <FiHash style={{ color: "#1e293b", fontSize: "1.2rem" }} />;

      // Performance specifications
    } else if (name.includes("speed") || name.includes("clock")) {
      return <FaBolt style={{ color: "#f59e0b", fontSize: "1.2rem" }} />;
    } else if (name.includes("temperature")) {
      return (
        <FaTemperatureHigh style={{ color: "#ef4444", fontSize: "1.2rem" }} />
      );
    } else if (name.includes("power") || name.includes("watt")) {
      return <FiCpu style={{ color: "#3b82f6", fontSize: "1.2rem" }} />;

      // Network specifications
    } else if (
      name.includes("network") ||
      name.includes("wifi") ||
      name.includes("bluetooth")
    ) {
      return (
        <FaNetworkWired style={{ color: "#10b981", fontSize: "1.2rem" }} />
      );
    } else if (
      name.includes("port") ||
      name.includes("usb") ||
      name.includes("hdmi")
    ) {
      return <FiCode style={{ color: "#64748b", fontSize: "1.2rem" }} />;

      // General specifications
    } else if (name.includes("type") || name.includes("category")) {
      return <FiType style={{ color: "#8b5cf6", fontSize: "1.2rem" }} />;
    } else if (name.includes("model") || name.includes("version")) {
      return <FiPackage style={{ color: "#3b82f6", fontSize: "1.2rem" }} />;
    } else if (name.includes("capacity")) {
      return (
        <MdOutlineStorage style={{ color: "#10b981", fontSize: "1.2rem" }} />
      );
    } else if (name.includes("standard") || name.includes("compliance")) {
      return <FiClipboard style={{ color: "#f59e0b", fontSize: "1.2rem" }} />;
    } else if (name.includes("certification")) {
      return <FiFileText style={{ color: "#06b6d4", fontSize: "1.2rem" }} />;
    } else if (name.includes("feature")) {
      return <FiList style={{ color: "#ec4899", fontSize: "1.2rem" }} />;

      // Manufacturing specifications
    } else if (name.includes("manufacturer") || name.includes("brand")) {
      return (
        <MdOutlineFactory style={{ color: "#1e293b", fontSize: "1.2rem" }} />
      );
    } else if (name.includes("origin") || name.includes("country")) {
      return <FiHome style={{ color: "#10b981", fontSize: "1.2rem" }} />;
    } else if (name.includes("warranty")) {
      return <FiSettings style={{ color: "#f59e0b", fontSize: "1.2rem" }} />;

      // Construction specifications
    } else if (name.includes("thickness") || name.includes("diameter")) {
      return <GiStoneBlock style={{ color: "#64748b", fontSize: "1.2rem" }} />;
    } else if (name.includes("chemical") || name.includes("composition")) {
      return (
        <GiChemicalTank style={{ color: "#ef4444", fontSize: "1.2rem" }} />
      );

      // Default icon
    } else {
      return <FiSettings style={{ color: "#6b7280", fontSize: "1.2rem" }} />;
    }
  };

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "30px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "700",
              color: "#1e293b",
              margin: "0 0 8px 0",
            }}
          >
            Specification Management
          </h1>
          <p
            style={{
              fontSize: "1.1rem",
              color: "#64748b",
              margin: "0",
            }}
          >
            Manage product specifications and technical details
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "12px",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
            transition: "all 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 8px 25px rgba(102, 126, 234, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.3)";
          }}
        >
          <FiPlus style={{ fontSize: "1.1rem" }} />
          Add New Specification
        </button>
      </div>

      {/* Statistics Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        {/* Total Specifications */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiSettings />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.total}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Total Specifications
            </p>
          </div>
        </div>

        {/* Active Specifications */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #10b981, #059669)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiCheck />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.active}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Active Specifications
            </p>
          </div>
        </div>

        {/* Inactive Specifications */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #ef4444, #dc2626)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiX />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.inactive}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Inactive Specifications
            </p>
          </div>
        </div>

        {/* Recent Specifications (Last 7 days) */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #f59e0b, #d97706)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiCalendar />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.recent}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Recent (7 days)
            </p>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          marginBottom: "25px",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            position: "relative",
            flex: "1",
            minWidth: "300px",
          }}
        >
          <FiSearch
            style={{
              position: "absolute",
              left: "15px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
              fontSize: "1.1rem",
            }}
          />
          <input
            type="text"
            placeholder="Search specifications by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 20px 12px 45px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              transition: "all 0.3s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#667eea";
              e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
          }}
        >
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              cursor: "pointer",
              minWidth: "140px",
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={() => {
              setSearchTerm("");
              setFilterStatus("all");
            }}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              cursor: "pointer",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.3s ease",
            }}
            onMouseOver={(e) => {
              e.target.style.background = "#f8fafc";
              e.target.style.color = "#475569";
            }}
            onMouseOut={(e) => {
              e.target.style.background = "white";
              e.target.style.color = "#64748b";
            }}
          >
            <FiX /> Clear Filters
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        }}
      >
        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 40px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f1f5f9",
                borderTop: "4px solid #667eea",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            ></div>
            <p
              style={{
                color: "#64748b",
                fontSize: "1rem",
                margin: "0",
              }}
            >
              Loading specifications...
            </p>
          </div>
        ) : sortedSpecifications.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 40px",
              color: "#64748b",
            }}
          >
            <FiSettings
              style={{
                fontSize: "4rem",
                color: "#cbd5e1",
                marginBottom: "20px",
              }}
            />
            <h3
              style={{
                fontSize: "1.5rem",
                color: "#475569",
                margin: "0 0 12px 0",
              }}
            >
              No specifications found
            </h3>
            <p
              style={{
                fontSize: "1rem",
                margin: "0 0 25px 0",
              }}
            >
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => setShowModal(true)}
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: "0 auto",
              }}
            >
              <FiPlus /> Add Your First Specification
            </button>
          </div>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("specification_name")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Specification Name
                    {sortConfig.key === "specification_name" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("status")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Status
                    {sortConfig.key === "status" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("cr_date")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Created Date
                    {sortConfig.key === "cr_date" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedSpecifications.map((spec) => {
                const statusConfig = getStatusConfig(spec.status);
                return (
                  <tr
                    key={spec.sid}
                    style={{
                      transition: "background 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = "#f8fafc";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = "white";
                    }}
                  >
                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        {getSpecificationIcon(spec.specification_name)}
                        <div style={{ flex: "1" }}>
                          <h4
                            style={{
                              fontWeight: "600",
                              color: "#1e293b",
                              margin: "0 0 4px 0",
                              fontSize: "1rem",
                              cursor: "pointer",
                            }}
                            onClick={() => handleView(spec)}
                          >
                            {spec.specification_name}
                          </h4>
                          <p
                            style={{
                              color: "#94a3b8",
                              fontSize: "0.875rem",
                              margin: "0",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <FiCalendar style={{ fontSize: "0.8rem" }} />
                            Updated:{" "}
                            {formatTime(spec.update_time || spec.cr_date)}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          background: statusConfig.background,
                          color: statusConfig.color,
                          border: `1px solid ${statusConfig.border}`,
                        }}
                      >
                        {statusConfig.icon}
                        {statusConfig.text}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#64748b",
                          fontSize: "0.875rem",
                        }}
                      >
                        <FiCalendar style={{ fontSize: "0.9rem" }} />
                        {formatDate(spec.cr_date)}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                        }}
                      >
                        <button
                          onClick={() => handleView(spec)}
                          style={{
                            background: "#3b82f6",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="View Details"
                        >
                          <FiEye />
                        </button>
                        <button
                          onClick={() => toggleStatus(spec.sid, spec.status)}
                          style={{
                            background:
                              spec.status === "active" ? "#f59e0b" : "#10b981",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title={
                            spec.status === "active" ? "Deactivate" : "Activate"
                          }
                        >
                          {spec.status === "active" ? (
                            <FiEyeOff />
                          ) : (
                            <FiCheck />
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(spec)}
                          style={{
                            background: "#f1f5f9",
                            color: "#475569",
                            border: "1px solid #e2e8f0",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(spec.sid)}
                          style={{
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: "1000",
            padding: "20px",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              animation: "modalSlideIn 0.3s ease",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <h2
                style={{
                  margin: "0",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                {editingSpecification
                  ? "Edit Specification"
                  : "Add New Specification"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "white",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: "30px" }}>
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Specification Name *
                </label>
                <input
                  type="text"
                  name="specification_name"
                  value={formData.specification_name}
                  onChange={handleInputChange}
                  placeholder="Enter specification name (e.g., Processor, RAM, Storage, Dimensions)"
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(102, 126, 234, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <p
                  style={{
                    margin: "8px 0 0 0",
                    color: "#94a3b8",
                    fontSize: "0.85rem",
                  }}
                >
                  Examples: "Processor", "RAM", "Storage", "Display Size",
                  "Weight"
                </p>
              </div>

              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Status
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "15px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      padding: "10px 15px",
                      borderRadius: "8px",
                      border: `2px solid ${formData.status === "active" ? "#10b981" : "#e2e8f0"}`,
                      background:
                        formData.status === "active" ? "#f0fdf4" : "white",
                      flex: "1",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value="active"
                      checked={formData.status === "active"}
                      onChange={handleInputChange}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          color: "#065f46",
                          fontWeight: "600",
                        }}
                      >
                        <FiCheck /> Active
                      </div>
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          color: "#94a3b8",
                          fontSize: "0.85rem",
                        }}
                      >
                        Specification will be available
                      </p>
                    </div>
                  </label>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      padding: "10px 15px",
                      borderRadius: "8px",
                      border: `2px solid ${formData.status === "inactive" ? "#ef4444" : "#e2e8f0"}`,
                      background:
                        formData.status === "inactive" ? "#fef2f2" : "white",
                      flex: "1",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value="inactive"
                      checked={formData.status === "inactive"}
                      onChange={handleInputChange}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          color: "#dc2626",
                          fontWeight: "600",
                        }}
                      >
                        <FiX /> Inactive
                      </div>
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          color: "#94a3b8",
                          fontSize: "0.85rem",
                        }}
                      >
                        Specification will be hidden
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div
                style={{
                  paddingTop: "25px",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  gap: "15px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  style={{
                    background: "transparent",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    padding: "12px 25px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = "#f8fafc";
                    e.target.style.color = "#475569";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = "#64748b";
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  style={{
                    background: processing
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 30px",
                    borderRadius: "10px",
                    cursor: processing ? "not-allowed" : "pointer",
                    fontSize: "1rem",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    boxShadow: processing
                      ? "none"
                      : "0 4px 15px rgba(102, 126, 234, 0.3)",
                    opacity: processing ? 0.7 : 1,
                  }}
                  onMouseOver={(e) => {
                    if (!processing) {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 8px 25px rgba(102, 126, 234, 0.4)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!processing) {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 15px rgba(102, 126, 234, 0.3)";
                    }
                  }}
                >
                  {processing
                    ? "Processing..."
                    : editingSpecification
                      ? "Update Specification"
                      : "Add Specification"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && viewingSpecification && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: "1000",
            padding: "20px",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              animation: "modalSlideIn 0.3s ease",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <h2
                style={{
                  margin: "0",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                Specification Details
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "white",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "30px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  marginBottom: "25px",
                  paddingBottom: "25px",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2rem",
                    color: "white",
                  }}
                >
                  {getSpecificationIcon(
                    viewingSpecification.specification_name,
                  )}
                </div>
                <div>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      fontSize: "0.8rem",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      background: getStatusConfig(viewingSpecification.status)
                        .background,
                      color: getStatusConfig(viewingSpecification.status).color,
                      border: `1px solid ${getStatusConfig(viewingSpecification.status).border}`,
                      marginBottom: "10px",
                    }}
                  >
                    {getStatusConfig(viewingSpecification.status).icon}
                    {getStatusConfig(viewingSpecification.status).text}
                  </div>
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "700",
                      color: "#1e293b",
                      margin: "0",
                    }}
                  >
                    {viewingSpecification.specification_name}
                  </h3>
                </div>
              </div>

              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "25px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "20px",
                  }}
                >
                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Specification ID
                    </h4>
                    <p
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "600",
                        color: "#1e293b",
                        margin: "0",
                        fontFamily: "'Roboto Mono', monospace",
                      }}
                    >
                      #{viewingSpecification.sid}
                    </p>
                  </div>

                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Created Date
                    </h4>
                    <p
                      style={{
                        fontSize: "1rem",
                        fontWeight: "500",
                        color: "#1e293b",
                        margin: "0",
                      }}
                    >
                      {formatDate(viewingSpecification.cr_date)}
                    </p>
                  </div>

                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Last Updated
                    </h4>
                    <p
                      style={{
                        fontSize: "1rem",
                        fontWeight: "500",
                        color: "#1e293b",
                        margin: "0",
                      }}
                    >
                      {formatDate(
                        viewingSpecification.update_time ||
                          viewingSpecification.cr_date,
                      )}
                    </p>
                  </div>

                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Time Created
                    </h4>
                    <p
                      style={{
                        fontSize: "1rem",
                        fontWeight: "500",
                        color: "#1e293b",
                        margin: "0",
                      }}
                    >
                      {formatTime(viewingSpecification.cr_date)}
                    </p>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingSpecification);
                  }}
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flex: "1",
                  }}
                >
                  <FiEdit /> Edit Specification
                </button>

                <button
                  onClick={() => setShowViewModal(false)}
                  style={{
                    background: "transparent",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                    flex: "1",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default SpecificationManagement;
